import type { ImageScenario } from './imageTypes';
import type { Partner, Tool } from './partners';
// Explicit .ts extension: this module is reached from src/scenarios/index.ts,
// which the build-time tools load through Node's native type stripping, and
// Node will not resolve an extensionless relative import. Vite is happy either
// way. Same rule as scenarios/index.ts.
import { setRuntimeRegistry } from './partners.ts';

/**
 * Runtime level content, layered over what is compiled into the bundle.
 *
 * Two kinds of thing arrive from the server:
 *
 *   levels     — whole levels an admin authored. They did not exist at build
 *                time and have no counterpart in src/scenarios.
 *   overrides  — patches on levels that DO ship in the bundle. Stored as a
 *                patch rather than a copy, so the committed scenario file
 *                stays the record of what was originally written and deleting
 *                the override restores it exactly.
 *
 * The merge is deliberately shallow-per-field rather than a deep merge: an
 * override that supplies `arrows` replaces the whole array. Half-merging two
 * arrow lists by index is how a defect ends up pointing at the wrong arrow,
 * and that is precisely the failure this game exists to teach people to spot.
 */

export interface RuntimeContent {
  levels: ImageScenario[];
  overrides: Record<string, Partial<ImageScenario>>;
  /** Tool categories an admin created. Merged over the compiled registry. */
  partners: Partner[];
  tools: Tool[];
  /** Level ids in display order. Anything not listed keeps its natural place. */
  order: string[];
  /**
   * Compiled levels an admin removed.
   *
   * They cannot be deleted — they ship in the bundle — so they are filtered out
   * here instead. Reversible, which deleting a scenario file would not be.
   */
  hidden: string[];
}

const EMPTY: RuntimeContent = {
  levels: [],
  overrides: {},
  partners: [],
  tools: [],
  order: [],
  hidden: [],
};

let current: RuntimeContent = EMPTY;

export function setRuntimeContent(next: RuntimeContent | null): void {
  current = next ?? EMPTY;
  // The registry has to be applied before anything reads a tool, or a level
  // belonging to an admin-created tool resolves to a placeholder.
  setRuntimeRegistry(current.partners, current.tools);
}

export function getRuntimeContent(): RuntimeContent {
  return current;
}

/**
 * Runtime levels that cannot be shown because a compiled level owns their id.
 *
 * Their content is safe on the server; it is simply unreachable. Surfacing this
 * is the difference between "my upload vanished" and "that id is taken".
 */
/** Compiled levels an admin has removed from the game. */
export function hiddenLevelIds(): string[] {
  return current.hidden;
}

export function shadowedLevelIds(compiled: readonly ImageScenario[]): string[] {
  const compiledIds = new Set(compiled.map((level) => level.id));
  return current.levels.filter((level) => compiledIds.has(level.id)).map((level) => level.id);
}

/** Apply the runtime layer to the compiled levels. */
export function mergeLevels(compiled: readonly ImageScenario[]): ImageScenario[] {
  const { levels, overrides, order, hidden } = current;
  const removed = new Set(hidden);

  const merged = compiled
    .filter((level) => !removed.has(level.id))
    .map((level) => {
      const patch = overrides[level.id];
      if (!patch) return level;
    // The id is pinned: scores are recorded against it, so an override must
    // never be able to rename a level into being a different one.
      return { ...level, ...patch, id: level.id } as ImageScenario;
    });

  // Admin-authored levels append after the compiled set, in the order the
  // server returned them, so adding one never renumbers the existing cases.
  //
  // A runtime level whose id is already taken by a compiled one is SKIPPED —
  // the compiled level owns that id, and its scenario file is the record. That
  // used to happen silently, so a level that saved successfully simply never
  // appeared, in the game or in the admin list. `shadowedLevelIds` exists so
  // the admin UI can say so.
  const existing = new Set(merged.map((level) => level.id));
  for (const level of levels) {
    if (!existing.has(level.id)) merged.push(level);
  }

  if (order.length === 0) return merged;

  // Explicit order first, then anything the list does not mention, still in its
  // natural position. A level added after the last reorder therefore appears at
  // the end rather than disappearing.
  const byId = new Map(merged.map((level) => [level.id, level]));
  const ordered: ImageScenario[] = [];
  const placed = new Set<string>();
  for (const id of order) {
    const level = byId.get(id);
    if (level && !placed.has(id)) {
      ordered.push(level);
      placed.add(id);
    }
  }
  for (const level of merged) {
    if (!placed.has(level.id)) ordered.push(level);
  }
  return ordered;
}

/**
 * Fetch the runtime layer.
 *
 * Never throws and never blocks the game: if the API is unreachable the
 * compiled levels are still a complete, playable campaign, and a player should
 * not get an error screen because an admin feature is down.
 */
export async function loadRuntimeContent(): Promise<boolean> {
  try {
    const response = await fetch('/api/content');
    if (!response.ok) return false;
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object') return false;
    const payload = body as {
      levels?: unknown;
      overrides?: unknown;
      partners?: unknown;
      tools?: unknown;
      order?: unknown;
      hidden?: unknown;
    };
    setRuntimeContent({
      levels: Array.isArray(payload.levels) ? (payload.levels as ImageScenario[]) : [],
      overrides:
        payload.overrides && typeof payload.overrides === 'object'
          ? (payload.overrides as Record<string, Partial<ImageScenario>>)
          : {},
      partners: Array.isArray(payload.partners) ? (payload.partners as Partner[]) : [],
      tools: Array.isArray(payload.tools) ? (payload.tools as Tool[]) : [],
      order: Array.isArray(payload.order) ? (payload.order as string[]) : [],
      hidden: Array.isArray(payload.hidden) ? (payload.hidden as string[]) : [],
    });
    return true;
  } catch {
    return false;
  }
}
